import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag, ExternalLink, Share2, Check, BookOpenText } from "lucide-react";
import { fetchBook, fetchBookExtracts, genreLabel, bookShareUrl } from "../lib/api";
import { SocialShareButtons } from "../components/SocialShareButtons";

export default function BookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [extract, setExtract] = useState(null);

  useEffect(() => {
    fetchBook(id)
      .then((b) => {
        setBook(b);
        document.title = `${b.title} — ${b.author} | Challenges Littéraires Éditions`;
      })
      .catch(() => setError(true));
    fetchBookExtracts(id).then((list) => setExtract(list[0] || null)).catch(() => setExtract(null));
    return () => { document.title = "CLED — Challenges Littéraires Éditions"; };
  }, [id]);

  const handleShare = async () => {
    const url = window.location.href;
    const text = `Découvrez "${book.title}" par ${book.author} chez Challenges Littéraires Éditions`;
    if (navigator.share) {
      try { await navigator.share({ title: book.title, text, url }); return; } catch (e) { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      window.prompt("Copiez le lien ci-dessous :", url);
    }
  };

  if (error) {
    return (
      <div className="pt-32 pb-20 px-6 md:px-12 lg:px-24 min-h-screen text-center">
        <h1 className="font-serif text-4xl mb-4 text-[#1A1A1A]">Livre introuvable</h1>
        <p className="text-[#757575] font-body mb-8">Ce livre n'existe pas ou a été retiré du catalogue.</p>
        <Link to="/livres" data-testid="back-to-catalog" className="inline-flex items-center gap-2 bg-[#2C402E] text-[#FAF9F6] px-6 py-3 font-body">
          <ArrowLeft size={16} /> Retour au catalogue
        </Link>
      </div>
    );
  }

  if (!book) {
    return <div className="pt-32 pb-20 px-6 md:px-12 lg:px-24 min-h-screen text-center text-[#757575]">Chargement…</div>;
  }

  const orderMailto = `mailto:challengeslitteraires.editions@gmail.com?subject=${encodeURIComponent("Commande — " + book.title)}&body=${encodeURIComponent("Bonjour,\n\nJe souhaite commander l'ouvrage « " + book.title + " » de " + book.author + ".\n\nMerci de me communiquer les modalités d'achat.\n\nCordialement,")}`;

  return (
    <div data-testid={`book-detail-page-${book.id}`} className="pt-32 pb-20 px-6 md:px-12 lg:px-24 min-h-screen">
      <button
        onClick={() => navigate(-1)}
        data-testid="book-detail-back"
        className="inline-flex items-center gap-2 text-sm font-body text-[#4A4A4A] hover:text-[#2C402E] mb-10 transition-colors"
      >
        <ArrowLeft size={16} /> Retour
      </button>

      <article className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 max-w-6xl">
        <div className="md:col-span-5">
          <div className="bg-[#F4F1EA] aspect-[3/4] flex items-center justify-center p-8 md:p-10">
            <img src={book.cover_url} alt={book.title} className="w-full max-w-[320px] shadow-2xl" />
          </div>
        </div>

        <div className="md:col-span-7">
          {book.is_new && (
            <span className="inline-block bg-[#9E4735] text-[#FAF9F6] text-[10px] tracking-[0.2em] uppercase font-body px-3 py-1 mb-4">
              Nouveauté
            </span>
          )}
          <p className="text-[10px] tracking-[0.25em] uppercase font-body text-[#9E4735] mb-3">
            {genreLabel(book.genre)}
            {book.collection && <span className="text-[#757575] normal-case tracking-normal"> · Collection {book.collection}</span>}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A] leading-tight font-light mb-3">
            {book.title}
          </h1>
          <p className="text-lg font-body text-[#4A4A4A] mb-8">
            par <em>{book.author}</em>
          </p>

          <div className="flex flex-wrap gap-x-8 gap-y-3 text-xs font-body text-[#757575] mb-8 pb-6 border-b border-[#D1CFC7]">
            {book.price && (
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735]">Prix</p>
                <p className="font-mono text-lg text-[#1A1A1A] mt-0.5">{book.price.toFixed(2)} €</p>
              </div>
            )}
            {book.publication_date && (
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735]">Parution</p>
                <p className="text-base text-[#1A1A1A] mt-0.5">
                  {new Date(book.publication_date).toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}
                </p>
              </div>
            )}
            {book.pages && (
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735]">Pages</p>
                <p className="text-base text-[#1A1A1A] mt-0.5">{book.pages}</p>
              </div>
            )}
            {book.isbn && (
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735]">ISBN</p>
                <p className="font-mono text-sm text-[#1A1A1A] mt-0.5">{book.isbn}</p>
              </div>
            )}
            {book.formats && book.formats.length > 0 && (
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735]">Formats</p>
                <p className="text-base text-[#1A1A1A] mt-0.5">
                  {book.formats.includes("papier_partenaires")
                    ? "Papier (Cameroun/Tchad)"
                    : book.formats.map((f) => f === "papier" ? "Papier" : "Ebook").join(" · ")}
                </p>
              </div>
            )}
          </div>

          <p className="text-base font-body text-[#1A1A1A] leading-relaxed whitespace-pre-line mb-10">
            {book.description}
          </p>

          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            {book.amazon_url ? (
              <a
                href={book.amazon_url}
                target="_blank"
                rel="noreferrer"
                data-testid={`page-buy-${book.id}`}
                className="inline-flex items-center justify-center gap-2 bg-[#2C402E] text-[#FAF9F6] hover:bg-[#1F2D21] px-8 py-3 font-body tracking-wide transition-all"
              >
                <ShoppingBag size={16} /> Acheter sur Amazon <ExternalLink size={14} />
              </a>
            ) : (
              <a
                href={orderMailto}
                data-testid={`page-order-${book.id}`}
                className="inline-flex items-center justify-center gap-2 bg-[#2C402E] text-[#FAF9F6] hover:bg-[#1F2D21] px-8 py-3 font-body tracking-wide transition-all whitespace-nowrap"
              >
                <ShoppingBag size={16} /> Commander auprès de CLED
              </a>
            )}
            {extract && (
              <Link
                to={`/histoires/${extract.slug}`}
                data-testid={`page-read-extract-${book.id}`}
                className="inline-flex items-center justify-center gap-2 border border-[#9E4735] text-[#9E4735] hover:bg-[#9E4735] hover:text-[#FAF9F6] px-8 py-3 font-body transition-colors"
              >
                <BookOpenText size={16} /> Lire un extrait offert
              </Link>
            )}
            <button
              onClick={handleShare}
              data-testid={`page-share-${book.id}`}
              className="inline-flex items-center justify-center gap-2 border border-[#2C402E] text-[#2C402E] hover:bg-[#2C402E] hover:text-[#FAF9F6] px-8 py-3 font-body transition-all"
            >
              {copied ? <><Check size={16} /> Lien copié !</> : <><Share2 size={16} /> Partager</>}
            </button>
          </div>
          <p className="text-xs text-[#757575] font-body mt-5">
            Ou commander via la{" "}
            <a
              href="https://www.facebook.com/share/1BbuPaUifd/"
              target="_blank"
              rel="noreferrer"
              className="text-[#2C402E] underline hover:text-[#9E4735] transition-colors"
            >
              page Facebook Pick & Read
            </a>
          </p>

          {/* Boutons de partage rapide */}
          <div className="mt-10 pt-6 border-t border-[#D1CFC7]">
            <SocialShareButtons
              url={bookShareUrl(book.id)}
              title={book.title}
              author={book.author}
              testId={`book-share-${book.id}`}
            />
          </div>
        </div>
      </article>
    </div>
  );
}
